// Ubary-style vertical admin sidebar. Replaces the category-grid AdminQuickActions
// on the admin dashboard with a sticky right-hand rail: brand at top, then a
// scrollable list of icon+label rows. The currently active row uses the
// primary brand color (matches the orange highlight from the reference mock —
// Talabya's own primary is blue, so we keep brand consistency).
import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import {
  Home, PackageCheck, Store, Wallet, Bike, Users, UserCheck, FileBarChart2,
  MapPin, Image as ImageIcon, DollarSign, CreditCard, Package, Crown, Phone,
  MessageCircle, FileAudio, Megaphone, ArrowDownToLine, XCircle, User, RefreshCw,
  Radio, ChevronDown, ChevronUp,
} from "lucide-react";

function Row({ icon: Icon, label, active, onClick, to, badge, testId, tone }) {
  const activeCls = active
    ? "bg-primary-brand text-white shadow-sm"
    : "text-[var(--text-primary)] hover:bg-blue-50";
  const toneCls = tone === "success" ? " text-emerald-700" : tone === "warning" ? " text-amber-700" : "";
  const content = (
    <div className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all ${activeCls} ${!active ? toneCls : ""}`}>
      {Icon && (
        <span className="shrink-0">
          <Icon size={18} className={active ? "" : "opacity-80"}/>
        </span>
      )}
      <span className="flex-1 min-w-0 text-right font-black text-sm truncate" style={{fontFamily:"Cairo"}}>
        {label}
      </span>
      {badge != null && badge > 0 && (
        <span className={`min-w-[20px] h-[20px] px-1 rounded-full text-[10px] font-black flex items-center justify-center shrink-0 ${
          active ? "bg-white text-primary-brand" : "bg-red-500 text-white ring-2 ring-white"
        }`}>
          {badge > 9 ? "9+" : badge}
        </span>
      )}
      {/* Right-edge highlight bar for the active row */}
      {active && (
        <span className="absolute right-0 top-2 bottom-2 w-1 rounded-l-full bg-white/70"/>
      )}
    </div>
  );
  const wrapper = "relative block";
  if (to) {
    return <Link to={to} className={wrapper} data-testid={testId}>{content}</Link>;
  }
  return (
    <button onClick={onClick} className={`${wrapper} w-full text-right`} type="button" data-testid={testId}>
      {content}
    </button>
  );
}

function Group({ title }) {
  return (
    <div className="px-4 pt-4 pb-1 text-[10px] font-black tracking-wider text-[var(--text-secondary)]/70 uppercase">
      {title}
    </div>
  );
}

export default function AdminSidebar({
  wsStatus,
  onRefresh,
  onReports, onUsers, onSales, onDrivers, onPricing, onApprovals, onCards,
  onPwResets, onSupport, onDuplicates, onMergeLog, onDriverWallets,
  onProfile, onTeam, onCancelRequests, onBroadcast, onCashouts,
  onTwilio, onBeta, onBanners, onCategorySettings,
  pendingResetsCount = 0, pendingCashoutsCount = 0, pendingCancelCount = 0,
}) {
  const { pathname } = useLocation();
  const isHome = pathname === "/admin" || pathname === "/admin/";
  const [showMore, setShowMore] = useState(false);

  return (
    <aside
      className="lg:sticky lg:top-4 self-start bg-white rounded-3xl border border-[var(--border)] talabya-shadow-soft overflow-hidden"
      data-testid="admin-sidebar"
      aria-label="التنقل الرئيسي للوحة الإدارة"
    >
      {/* Brand */}
      <div className="px-4 py-4 border-b border-[var(--border)] flex items-center gap-3">
        <div className="w-10 h-10 rounded-2xl bg-primary-brand text-white flex items-center justify-center shrink-0">
          <Home size={18}/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-black text-base leading-none" style={{fontFamily:"Cairo"}}>طلبية</div>
          <div className="text-[10px] text-[var(--text-secondary)] mt-1 flex items-center gap-1">
            <Radio size={9} className={wsStatus === "open" ? "text-emerald-500" : "text-amber-500"}/>
            بث لحظي {wsStatus === "open" ? "متصل" : "..."}
          </div>
        </div>
        <button
          onClick={onRefresh}
          className="w-8 h-8 rounded-lg hover:bg-blue-50 text-[var(--text-secondary)] flex items-center justify-center"
          title="تحديث"
          data-testid="admin-sidebar-refresh"
        >
          <RefreshCw size={14}/>
        </button>
      </div>

      {/* Nav — primary */}
      <nav className="p-2 space-y-1 max-h-[calc(100vh-160px)] overflow-y-auto">
        <Row icon={Home}          label="الرئيسية"       active={isHome}                       to="/admin"                testId="side-home"/>
        <Row icon={PackageCheck}  label="الطلبات"        active={pathname.startsWith("/admin/orders")}       to="/admin/orders"         testId="side-orders"/>
        <Row icon={Store}         label="المحلات"        active={pathname.startsWith("/admin/restaurants")}  to="/admin/restaurants"    testId="side-restaurants"/>
        <Row icon={MapPin}        label="الخريطة الحية"  active={pathname.startsWith("/admin/live-map")}     to="/admin/live-map"       testId="side-livemap"/>
        <Row icon={Bike}          label="السائقون"       onClick={onDrivers}                                  testId="side-drivers"/>
        <Row icon={Users}         label="المستخدمون"     onClick={onUsers}                                    testId="side-users"/>
        <Row icon={UserCheck}     label="الحسابات المعلّقة" onClick={onApprovals}                             testId="side-approvals"/>
        <Row icon={FileBarChart2} label="التقارير"       onClick={onReports}                                  testId="side-reports"/>
        <Row icon={DollarSign}    label="MyPay"          active={pathname.startsWith("/admin/mypay")}         to="/admin/mypay"          testId="side-mypay"/>
        <Row icon={ImageIcon}     label="البانرات"       onClick={onBanners}                                  testId="side-banners" tone="success"/>
        <Row icon={Store}         label="إعدادات الأقسام" onClick={onCategorySettings}                        testId="side-cat-settings"/>
        <Row icon={Megaphone}     label="إشعار جماعي"    onClick={onBroadcast}                                testId="side-broadcast" tone="success"/>
        <Row icon={ArrowDownToLine} label="طلبات السحب"  onClick={onCashouts} badge={pendingCashoutsCount}    testId="side-cashouts" tone="warning"/>
        <Row icon={XCircle}       label="طلبات الإلغاء"  onClick={onCancelRequests} badge={pendingCancelCount} testId="side-cancels"/>

        <button
          onClick={() => setShowMore((v) => !v)}
          className="w-full mt-2 px-4 py-2 rounded-xl text-[11px] font-black text-[var(--text-secondary)] hover:bg-blue-50 flex items-center justify-center gap-1"
          data-testid="side-toggle-more"
          type="button"
        >
          {showMore ? "إخفاء المزيد" : "المزيد"}
          {showMore ? <ChevronUp size={12}/> : <ChevronDown size={12}/>}
        </button>

        {showMore && (
          <div className="space-y-1 pt-1">
            <Group title="المدفوعات"/>
            <Row icon={CreditCard} label="كروت الشحن"      onClick={onCards}         testId="side-cards"/>
            <Row icon={Wallet}     label="محافظ السائقين"   onClick={onDriverWallets} testId="side-driver-wallets"/>
            <Row icon={Wallet}     label="أسعار التوصيل"   onClick={onPricing}       testId="side-pricing"/>

            <Group title="المستخدمون"/>
            <Row icon={UserCheck}  label="استعادة كلمة المرور" onClick={onPwResets} badge={pendingResetsCount} testId="side-pwresets"/>
            <Row icon={UserCheck}  label="الحسابات المكرّرة"    onClick={onDuplicates}                          testId="side-duplicates"/>
            <Row icon={FileBarChart2} label="سجل الدمج"          onClick={onMergeLog}                            testId="side-mergelog"/>

            <Group title="الإعدادات"/>
            <Row icon={User}       label="ملفي الشخصي"     onClick={onProfile} testId="side-profile"/>
            <Row icon={Crown}      label="فريق الإدارة"    onClick={onTeam}    testId="side-team"/>
            <Row icon={Phone}      label="أرقام الدعم"     onClick={onSupport} testId="side-support"/>
            <Row icon={MessageCircle} label="إعدادات WhatsApp" onClick={onTwilio} testId="side-twilio"/>
            <Row icon={Package}    label="اختبار APK"      onClick={onBeta}    testId="side-beta"/>
            <Row icon={FileAudio}  label="سجلات المكالمات" to="/admin/call-recordings" active={pathname.startsWith("/admin/call-recordings")} testId="side-call-recordings"/>
            <Row icon={FileBarChart2} label="المبيعات اليومية" onClick={onSales} testId="side-sales"/>
          </div>
        )}
      </nav>
    </aside>
  );
}
